import { useContext, useEffect, useState } from "react";
import AppContext from "../context/AppContext";
import ApiService from "../api/ApiService";
import notification from "../helpers/notification";
import "../css/RecordingOfScores.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import MonthDropdown from "../components/common/MonthDropdown";
import YearDropdown from "../components/common/YearDropdown";
import CalendarComponent from "../components/common/CalendarComponent";

// Define points per book type
const POINTS_PER_BOOK = {
  ChaitanyaCharitamritaSet: 36.0,
  SrimadBhagavatamSet: 72.0,
  MahabigBooks: 2.0,
  BigBooks: 1.0,
  MediumBooks: 0.5,
  SmallBooks: 0.25,
  SrilaPrabhupadaLilamrita: 14,
  Magazines: 0.1,
  "1 Year BTG Subscription-1": 5.0,
  "1 Year BTG Subscription-2": 2.5,
};

const INITIAL_COUNTS = Object.fromEntries(
  Object.keys(POINTS_PER_BOOK).map((key) => [key, 0])
);
const INITIAL_POINTS = Object.fromEntries(
  Object.keys(POINTS_PER_BOOK).map((key) => [key, 0])
);

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RecordingOfScores = () => {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  console.log("User's Temple", user);

  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState(INITIAL_COUNTS);
  const [sankirtanDate, setSankirtanDate] = useState("");
  const [participantCount, setParticipantCount] = useState(0);
  const [isOutreachNewLocation, setIsOutreachNewLocation] = useState("");
  const [isMediaUploaded, setIsMediaUploaded] = useState("");
  const [points, setPoints] = useState(INITIAL_POINTS);
  const [selectedEvent, setSelectedEvent] = useState("Regular Update");
  const [userScoreData, setUserScoreData] = useState({});
  const [harinamCount, setHarinaamCount] = useState(0);
  const [isHarinamComplete, setIsHarinamComplete] = useState(false);
  const [harinamCounts, setHarinaamCounts] = useState(0);
  const [location, setLocation] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sankirtanDuration, setSankirtanDuration] = useState("");
  const [localPoints, setLocalPoints] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    monthNames[new Date().getMonth()]
  );
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [displayMonth, setDisplayMonth] = useState(
    monthNames[new Date().getMonth()]
  );
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [resetCalendarTrigger, setResetCalendarTrigger] = useState(false);

  const handleDateTimeSelect = (dateTime) => {
    setSelectedDateTime(dateTime);
    setSelectedMonth(monthNames[dateTime.date.getMonth()]);
    setSelectedYear(dateTime.date.getFullYear());
  };

  const harinaamScheduleSave = async () => {
    if (!selectedDateTime?.date || !selectedDateTime?.time) {
      notification(
        "error",
        t("Date & time cannot be empty, Please select both date and time")
      );
      return;
    }

    // if (!phoneNumber || phoneNumber.length !== 10) {
    //   notification("error", t("Phone Number cannot be empty, Please enter a valid 10-digit phone number"));
    //   return;
    // }

    // if (!location) {
    //   notification(
    //     "error",
    //     t("Location cannot be empty, Please enter location.")
    //   );
    //   return;
    // }

    const { hour, minute, period } = selectedDateTime.time;
    const formattedTime = `${hour}:${minute
      .toString()
      .padStart(2, "0")} ${period}`;

    const data = [
      {
        location: location,
        temple: user.temple_name,
        date: selectedDateTime.date.toISOString(),
        time: formattedTime,
        phone_number: phoneNumber, // <-- Add phone number here
      },
    ];

    try {
      await ApiService.saveSchedule(data);
      notification("success", t("Sankirtan scheduled successfully"));
      setSelectedDateTime(null);
      setLocation("");
      setIsHarinamComplete(false);
      setPhoneNumber(""); // Reset phone number input
      setResetCalendarTrigger((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Scheduling Error")
      );
    }
  };

  const handleRecordingScoreSave = async () => {
    if (!sankirtanDuration) {
      notification("warning", t("Please enter duration of Sankirtan."));
      return;
    }

    if (!sankirtanDate) {
      notification("warning", t("Please select Sankirtan date."));
      return;
    }

    const month = new Date(sankirtanDate).getMonth() + 1; // Extract numeric month (1-12)
    const year = new Date(sankirtanDate).getFullYear(); // Extract numeric month (1-12)

    // 🧮 Points Calculation
    // 🧮 Points Calculation (Duration + Date Only)
    const durationPointsMap = {
      "0-30 mins": 0,
      "30-60 mins": 5,
      "60-90 mins": 10,
      "90-120 mins": 15,
      "120-150 mins": 20,
      "150-180 mins": 25,
    };

    // Points for duration
    const durationPoints = durationPointsMap[sankirtanDuration] || 0;

    // +5 bonus points if date is selected
    const datePoints = sankirtanDate ? 5 : 0;

    // Final total points
    const totalPoints = durationPoints + datePoints;

    const data = [
      {
        year: year,
        count_book: 1,
        points: totalPoints,
        participants: participantCount,
        outreach: isOutreachNewLocation,
        social_media: isMediaUploaded,
        user_id: user.id,
        user_name: user.name,
        hindiname: user.hindiname,
        temple_id: user.temple_id,
        temple_name: user.temple_name,
        hindi_temple_name: user.hinditempleName,
        temple: user.temple,
        state_id: user.state_id,
        state_name: user.state_name,
        zone: user.zone_name,
        event: "Regular Update",
        sankirtan_date: sankirtanDate, // ⬅️ ISO string from input
        duration: sankirtanDuration, // ⬅️ new field
        month, // ⬅️ extracted numeric month
      },
    ];

    try {
      setLoading(true);
      await ApiService.recordingScoreSubmit(data);
      notification("success", t("Score Submitted Successfully"));
      setSankirtanDate("");
      setParticipantCount(0);
      setIsOutreachNewLocation("");
      setSankirtanDuration("");
      setIsMediaUploaded("");
      getScoreCountData();
      fetchDataFromAllLiveRankingByUserId();
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Submission failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const getScoreCountData = async () => {
    try {
      const res = await ApiService.fetchScoreCount(
        user.temple_id,
        "all",
        true,
        displayYear,
        displayMonth
      );
      if (Array.isArray(res) && res.length) {
        setHarinaamCounts(parseInt(res[0].count_book, 10) || 0);
      } else {
        setHarinaamCounts(0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDataFromAllLiveRankingByUserId = async () => {
    try {
      if (user) {
        const res = await ApiService.fetchDataFromAllLiveRankingByUserId(
          user.temple_id,
          user.zone_name,
          user.temple,
          user.state_id,
          selectedEvent
        );
        setUserScoreData(res);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const calculatePoints = (bookType) => {
      const count = counts[bookType];
      const pointPerBook = POINTS_PER_BOOK[bookType];
      setPoints((prev) => ({ ...prev, [bookType]: count * pointPerBook }));
    };
    Object.keys(counts).forEach(calculatePoints);
  }, [counts]);

  useEffect(() => {
    if (user) {
      getScoreCountData();
      fetchDataFromAllLiveRankingByUserId();
    }
  }, [user, selectedEvent, selectedYear, selectedMonth]);

  useEffect(() => {
    if (user) {
      getScoreCountData();
    }
  }, [displayMonth, displayYear]);

  useEffect(() => {
    const participantPoints = participantCount * 5;
    const outreachPoints = isOutreachNewLocation === "Yes" ? 5 : 0;
    const mediaPoints = isMediaUploaded === "Yes" ? 5 : 0;

    const durationPointsMap = {
      "0-30 mins": 0,
      "30-60 mins": 5,
      "60-90 mins": 10,
      "90-120 mins": 15,
      "120-150 mins": 20,
      "150-180 mins": 25,
    };
    const durationPoints = durationPointsMap[sankirtanDuration] || 0;

    // ✅ Add +5 points if Sankirtan date is selected
    const datePoints = sankirtanDate ? 5 : 0;

    setLocalPoints(
      participantPoints +
        outreachPoints +
        mediaPoints +
        durationPoints +
        datePoints
    );
  }, [
    participantCount,
    isOutreachNewLocation,
    isMediaUploaded,
    sankirtanDuration,
    sankirtanDate, // ← include sankirtanDate in dependency
  ]);

  return (
    <>
      <ToastContainer />
      <div className="container common-container pledge-responsive update-responsive">
        <div className="update-score-container">
          <div className="row">
            <div className="col-md-6">
              <div className="card card-shadow">
                <div className="card-body">
                  <h5 className="card-title">
                    {t("Update Your Sankirtan Score")}
                  </h5>
                  <p className="card-text">
                    {t(
                      "View and update your harinaam sankirtan score to stay on track with your monthly commitment."
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card card-shadow">
                <div className="card-body">
                  <h5 className="stats-header">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.4744 17.0183H5.51784C2.96254 17.0183 1.0022 16.0954 1.55903 12.3807L2.20739 7.34633C2.55064 5.49279 3.73294 4.78341 4.77032 4.78341H13.2524C14.305 4.78341 15.4187 5.54618 15.8153 7.34633L16.4637 12.3807C16.9366 15.6759 15.0297 17.0183 12.4744 17.0183Z"
                        stroke="#130F26"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.5889 4.60034C12.5889 2.61194 10.977 1.00003 8.98861 1.00003V1.00003C8.03111 0.995975 7.11143 1.3735 6.43294 2.04913C5.75445 2.72476 5.37304 3.64283 5.37305 4.60034H5.37305"
                        stroke="#130F26"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M11.46 8.3532H11.4219"
                        stroke="#130F26"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M6.60114 8.3532H6.563"
                        stroke="#130F26"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    &nbsp;{t("Your Stats")} {""}
                    {selectedEvent !== "Select Event" &&
                      selectedEvent !== "Regular Update" &&
                      t("in") + " " + t(selectedEvent)}
                  </h5>

                  <div className="card-text stats">
                    <div className="text-center">
                      <p className="ranking-status-text">
                        {t("All India Rank")}
                      </p>
                      <span className="ranking-count">
                        {userScoreData.all_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("State Rank")}</p>
                      <span className="ranking-count">
                        {userScoreData.state_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("Zonal Rank")}</p>
                      <span className="ranking-count">
                        {userScoreData.zone_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("Total Points")}</p>
                      <span className="ranking-count">
                        {userScoreData?.points
                          ? Math.ceil(userScoreData?.points)?.toLocaleString(
                              "en-IN"
                            )
                          : "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">
                        {t("Sankirtan Count")}
                      </p>
                      <span className="ranking-count">
                        {userScoreData?.count_book
                          ? Math.ceil(
                              userScoreData?.count_book
                            )?.toLocaleString("en-IN")
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="update-score-container mt-4">
            <div className="row">
              <div className="col-6">
                <div className="content-table">
                  <div className="d-flex justify-content-between">
                    <div className="table-description">
                      <h3 className="content-title">
                        {t("Schedule Your Next Harinaam Sankirtan")}
                      </h3>
                      <p className="text-muted text-main-class">
                        {t(
                          "Please enter date, time and location of sankirtan."
                        )}
                      </p>
                    </div>
                  </div>
                  <hr />

                  <div className="card-body">
                    {/* Replaced with Calendar Modal Component */}
                    <CalendarComponent
                      onDateTimeSelect={handleDateTimeSelect}
                      resetTrigger={resetCalendarTrigger}
                    />
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Enter Location")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Enter the location of Sankirtan.")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          style={{ textAlign: "center" }}
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                        />
                      </div>
                    </div>
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Enter Phone Number")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Enter the mobile number")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          value={phoneNumber}
                          onChange={(e) => {
                            // Remove non-digit characters and limit length to 10
                            const cleaned = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            setPhoneNumber(cleaned);
                          }}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div> */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-6 fw-medium mobile-font">
                        {t("Total Harinam Sankirtan Count")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Total Harinaam Sankirtan performed")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          disabled
                          value={userScoreData?.count_book || "0"}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-6 fw-medium mobile-font">
                        {t("Your Total Harinaam Sankirtan Score")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Total Harinaam Sankirtan score")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          disabled
                          value={userScoreData?.points || "0"}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                  <hr style={{ marginTop: "5%" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted col-md-7 text-main-class">
                      {t("If you wish to do any changes, please contact us.")}
                    </small>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={harinaamScheduleSave}
                    >
                      {t("Schedule Sankirtan")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-6">
                <div className="content-table">
                  <div className="d-flex justify-content-between">
                    <div className="table-description">
                      <h3 className="content-title">
                        {t("Update Your Harinaam Sankirtan Score")}
                      </h3>
                      <p className="text-muted text-main-class">
                        {t("Please enter your Harinaam Sankirtan score")}
                      </p>
                    </div>
                  </div>
                  <hr />

                  <div className="card-body">
                    {/* Date of Sankirtan */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Date of Sankirtan")}
                        <br />
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Select the date of the month.")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="date"
                          className="form-control"
                          style={{
                            colorScheme: "light",
                            color: sankirtanDate ? "#000" : "#6b7280",
                          }}
                          value={sankirtanDate}
                          onChange={(e) => setSankirtanDate(e.target.value)}
                          max={new Date().toISOString().split("T")[0]} // This sets today as the max date
                        />
                      </div>
                    </div>

                    {/* Number of Participants */}
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Number of Participants")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Each participant earns 5 points.")}{" "}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="number"
                          min={0}
                          max={1000}
                          className="form-control placeholder:text-gray-500 text-black"
                          style={{ textAlign: "left" }}
                          placeholder="0"
                          value={participantCount === 0 ? "" : participantCount}
                          onChange={(e) => {
                            const val = e.target.value;
                            setParticipantCount(
                              val === "" ? 0 : Math.min(Number(val), 1000)
                            );
                          }}
                        />
                      </div>
                    </div> */}

                    {/* Outreach to New Location? */}
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Outreach to New Location?")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Yes = +5 points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: isOutreachNewLocation ? "#000" : "#6b7280",
                          }}
                          value={isOutreachNewLocation}
                          onChange={(e) =>
                            setIsOutreachNewLocation(e.target.value)
                          }
                        >
                          <option
                            value=""
                            disabled
                            style={{ color: "#6b7280" }}
                          >
                            {t("Select")}
                          </option>
                          <option value="Yes" style={{ color: "#000" }}>
                            {t("Yes")}
                          </option>
                          <option value="No" style={{ color: "#000" }}>
                            {t("No")}
                          </option>
                        </select>
                      </div>
                    </div> */}

                    {/* Images/Videos uploaded to Social Media */}
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Images/Videos uploaded to Social Media?")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Yes = +5 points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: isMediaUploaded ? "#000" : "#6b7280",
                          }}
                          value={isMediaUploaded}
                          onChange={(e) => setIsMediaUploaded(e.target.value)}
                        >
                          <option
                            value=""
                            disabled
                            style={{ color: "#6b7280" }}
                          >
                            {t("Select")}
                          </option>
                          <option value="Yes" style={{ color: "#000" }}>
                            {t("Yes")}
                          </option>
                          <option value="No" style={{ color: "#000" }}>
                            {t("No")}
                          </option>
                        </select>
                      </div>
                    </div> */}

                    {/* Duration of Sankirtan */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Duration of Sankirtan")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Longer Sankirtan earns more points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: sankirtanDuration ? "#000" : "#6b7280",
                          }}
                          value={sankirtanDuration}
                          onChange={(e) => setSankirtanDuration(e.target.value)}
                        >
                          <option value="" disabled>
                            {t("Select Duration")}
                          </option>
                          <option value="0-30 mins">0–30 mins (0 pts)</option>
                          <option value="30-60 mins">30–60 mins (5 pts)</option>
                          <option value="60-90 mins">
                            60–90 mins (10 pts)
                          </option>
                          <option value="90-120 mins">
                            90–120 mins (15 pts)
                          </option>
                          <option value="120-150 mins">
                            120–150 mins (20 pts)
                          </option>
                          <option value="150-180 mins">
                            150–180 mins (25 pts)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <hr style={{ marginTop: "6%" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <div
                      className="d-flex gap-3"
                      style={{
                        width: "40%",
                        backgroundColor: "#FBBF2426",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "8px",
                      }}
                    >
                      <svg
                        width="21"
                        height="18"
                        viewBox="0 0 21 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M20.25 18H0V0H20.25V18Z" stroke="#E5E7EB" />
                        <g clip-path="url(#clip0_6362_549)">
                          <path
                            d="M14.0625 0H6.1875C5.25586 0 4.49648 0.766406 4.53164 1.69453C4.53867 1.88086 4.5457 2.06719 4.55625 2.25H0.84375C0.376172 2.25 0 2.62617 0 3.09375C0 6.34922 1.17773 8.61328 2.75977 10.1496C4.31719 11.6648 6.21563 12.4277 7.61484 12.8145C8.4375 13.043 9 13.7285 9 14.4176C9 15.1523 8.40234 15.75 7.66758 15.75H6.75C6.12773 15.75 5.625 16.2527 5.625 16.875C5.625 17.4973 6.12773 18 6.75 18H13.5C14.1223 18 14.625 17.4973 14.625 16.875C14.625 16.2527 14.1223 15.75 13.5 15.75H12.5824C11.8477 15.75 11.25 15.1523 11.25 14.4176C11.25 13.7285 11.809 13.0395 12.6352 12.8145C14.0379 12.4277 15.9363 11.6648 17.4937 10.1496C19.0723 8.61328 20.25 6.34922 20.25 3.09375C20.25 2.62617 19.8738 2.25 19.4062 2.25H15.6937C15.7043 2.06719 15.7113 1.88437 15.7184 1.69453C15.7535 0.766406 14.9941 0 14.0625 0ZM1.71914 3.9375H4.68633C5.00625 7.10508 5.71289 9.22148 6.51094 10.6383C5.63555 10.2516 4.725 9.70664 3.9375 8.94023C2.8125 7.84688 1.89844 6.26836 1.72266 3.9375H1.71914ZM16.316 8.94023C15.5285 9.70664 14.618 10.2516 13.7426 10.6383C14.5406 9.22148 15.2473 7.10508 15.5672 3.9375H18.5344C18.3551 6.26836 17.441 7.84688 16.3195 8.94023H16.316Z"
                            fill="#FBBF24"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_6362_549">
                            <path d="M0 0H20.25V18H0V0Z" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>

                      <p className="text-black col-md-7 text-center">
                        {t("Total Points")}:{" "}
                        <span
                          className="text-yellow-400"
                          style={{ color: "#FBBF24" }}
                        >
                          {localPoints}
                        </span>
                      </p>
                    </div>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={handleRecordingScoreSave}
                    >
                      {t("Update Score")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecordingOfScores;
